/**
 * AtriRelativesInfoTable Component
 * Displays D24 Atri Muni Relatives system for family relationships
 * 24 divisions: 12 signs × 2 halves = 24 Horas with specific relatives
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateAtriRelatives,
  AtriRelativesData,
  AtriRelativesAttributes,
  HORA_DESCRIPTIONS,
  getHoraType
} from '../data/AtriRelativesTableData';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface AtriRelativesInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Group divisions by Hora number for display
 */
interface HoraRow {
  horaNumber: number;
  horaType: any;
  planets: AtriRelativesAttributes[];
}

/**
 * Create all 24 Hora rows with planets grouped
 */
function groupByHoraNumber(divisions: AtriRelativesAttributes[]): HoraRow[] {
  const rows: HoraRow[] = [];
  const planetsByHora: Record<number, AtriRelativesAttributes[]> = {};

  // Initialize all 24 Horas
  for (let i = 1; i <= 24; i++) {
    planetsByHora[i] = [];
  }

  // Group planets by Hora number
  divisions.forEach(div => {
    planetsByHora[div.horaNumber].push(div);
  });

  // Create rows with all info
  for (let i = 1; i <= 24; i++) {
    const planetsInHora = planetsByHora[i];
    const horaType = getHoraType(i);

    rows.push({
      horaNumber: i,
      horaType,
      planets: planetsInHora
    });
  }

  return rows;
}

export const AtriRelativesInfoTable: React.FC<AtriRelativesInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Calculate Atri Relatives divisions
  const relativesData: AtriRelativesData = calculateAtriRelatives(apiResponse);

  if (!relativesData.divisions.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Atri Relatives divisions</Text>
      </Box>
    );
  }

  // Group all divisions by Hora number (1-24)
  const horaRows = groupByHoraNumber(relativesData.divisions);

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      {/* Atri Relatives Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.small}>Sign</Th>
              <Th {...COLUMN_STYLES.medium}>Ruler</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Relative</Th>
            </Tr>
          </Thead>
          <Tbody>
            {horaRows.map((row) => {
              const horaInfo = HORA_DESCRIPTIONS[row.horaType as keyof typeof HORA_DESCRIPTIONS];

              return (
                <Tr key={row.horaNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                    {row.planets.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {row.planets.map((division, index) => {
                          // Use centralized planet display helper (DRY)
                          const planetDisplay = createPlanetDisplay(division.planet, currentHoraPlanet);
                          const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${division.planet.short_name}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planetDisplay.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Part Number */}
                  <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                    <Text fontSize="xs">
                      {row.horaNumber}
                    </Text>
                  </Td>

                  {/* Sign */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text fontSize="xs" fontWeight="normal">
                      {horaInfo.sign}
                    </Text>
                  </Td>

                  {/* Ruler */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text fontSize="xs" fontWeight="normal">
                      {horaInfo.ruler}
                    </Text>
                  </Td>

                  {/* Relative */}
                  <Td {...TABLE_TD_SMALL_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {horaInfo.relative}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
